import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {v4 as uuidv4} from 'uuid';
import type {GrowlAction, ShowMessageGrowlProps} from '@Libraries/message-growl.types';
import {DEFAULT_MESSAGE_TIMOUT_DURATION, GROWL_TYPE} from '@Libraries/message-growl.types';

export interface MessageGrowlItem {
  text: string;
  show: boolean;
  interval: number;
  type: GROWL_TYPE;
  key: string;
  showCloseIcon: boolean;
  icon: string;
  iconClassName?: string;
  dangerouslySetInnerHTML: boolean;
  subText?: string;
  progress?: number;
  isLoading?: boolean;
  sticky?: boolean;
  action?: GrowlAction;
}

export interface MessageGrowlState {
  growls: MessageGrowlItem[];
}

const initialState: MessageGrowlState = {
  growls: [],
};

const buildGrowlItem = (payload: ShowMessageGrowlProps): MessageGrowlItem => {
  return {
    text: payload.text,
    key: payload.key ?? uuidv4(),
    interval: payload.interval ?? DEFAULT_MESSAGE_TIMOUT_DURATION,
    type: payload.type ?? GROWL_TYPE.SUCCESS,
    show: true,
    showCloseIcon: payload.showCloseIcon ?? true,
    icon: payload.icon ?? '',
    iconClassName: payload.iconClassName,
    dangerouslySetInnerHTML: payload.dangerouslySetInnerHTML ?? false,
    subText: payload.subText,
    progress: payload.progress,
    isLoading: payload.isLoading,
    sticky: payload.sticky,
    action: payload.action,
  };
};

const messageGrowlSlice = createSlice({
  name: 'messageGrowl',
  initialState,
  reducers: {
    updateMessageGrowl: (state, action: PayloadAction<ShowMessageGrowlProps>) => {
      const key = action.payload.key ?? uuidv4();
      const existingIndex = state.growls.findIndex((g) => {
        return g.key === key;
      });

      const item = buildGrowlItem({...action.payload, key});

      if (existingIndex >= 0) {
        state.growls[existingIndex] = item;
      } else {
        state.growls.push(item);
      }
    },
    hideMessageGrowl: (state, action: PayloadAction<string>) => {
      const growl = state.growls.find((g) => {
        return g.key === action.payload;
      });
      if (growl) {
        growl.show = false;
      }
    },
    removeMessageGrowl: (state, action: PayloadAction<string>) => {
      state.growls = state.growls.filter((g) => {
        return g.key !== action.payload;
      });
    },
    updateMessageGrowlProgress: (state, action: PayloadAction<{key: string; progress: number}>) => {
      const growl = state.growls.find((g) => {
        return g.key === action.payload.key;
      });
      if (growl) {
        growl.progress = action.payload.progress;
      }
    },
  },
});

export const {updateMessageGrowl, hideMessageGrowl, removeMessageGrowl, updateMessageGrowlProgress} = messageGrowlSlice.actions;
export const messageGrowlReducer = messageGrowlSlice.reducer;
